"use client";

import { useState, useEffect, useRef } from "react";
import { EndpointTester } from "./endpoint-tester";
import { DatabaseSchemaView } from "./database-schema-view";
import { DatabaseQueryTester } from "./database-query-tester";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Connection } from "@/lib/hooks/use-connections";
import { getAuthToken } from "@/lib/auth";
import { API_ENDPOINTS } from "@/lib/api-config";
import {
  Code,
  Database,
  Lock,
  History,
  MapPin,
  Play,
  Loader2,
  RefreshCw,
  Upload,
  Table,
} from "lucide-react";

// Sensitive field patterns to obfuscate
const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /key/i,
  /api[_-]?key/i,
  /auth/i,
  /credential/i,
  /bearer/i,
  /private/i,
  /client[_-]?secret/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /session/i,
];

// Function to check if a key is sensitive
const isSensitiveKey = (key: string): boolean => {
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(key));
};

// Function to obfuscate sensitive data in objects
const obfuscateData = (data: any, depth = 0): any => {
  if (depth > 10) return data; // Prevent infinite recursion

  if (typeof data !== "object" || data === null) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => obfuscateData(item, depth + 1));
  }

  const obfuscated: any = {};
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveKey(key)) {
      // Obfuscate sensitive values
      if (typeof value === "string" && value.length > 0) {
        obfuscated[key] = "••••••••";
      } else {
        obfuscated[key] = "••••••••";
      }
    } else if (typeof value === "object" && value !== null) {
      // Recursively obfuscate nested objects
      obfuscated[key] = obfuscateData(value, depth + 1);
    } else {
      obfuscated[key] = value;
    }
  }
  return obfuscated;
};

interface ConnectionDetailsDialogProps {
  connection: Connection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: string;
}

export function ConnectionDetailsDialog({
  connection,
  open,
  onOpenChange,
  defaultTab = "config",
}: ConnectionDetailsDialogProps) {
  const [endpoints, setEndpoints] = useState<any>(null);
  const [errorHistory, setErrorHistory] = useState<any[]>([]);
  const [memories, setMemories] = useState<any[]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [openApiUrl, setOpenApiUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [databaseSchema, setDatabaseSchema] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDatabase = connection?.type === "database";

  useEffect(() => {
    // Reset state when dialog opens with new connection
    if (open && connection) {
      setEndpoints(null);
      setErrorHistory([]);
      setMemories([]);

      // Load existing OpenAPI URL from connection config
      const existingOpenApiUrl = connection.config?.openapi_url;
      if (existingOpenApiUrl) {
        setOpenApiUrl(existingOpenApiUrl);
        console.log("Loaded OpenAPI URL from connection:", existingOpenApiUrl);
      } else {
        setOpenApiUrl("");
      }

      fetchEndpoints();
      fetchErrorHistory();
      fetchMemories();
    }
  }, [connection?.id, open]);

  const fetchEndpoints = async () => {
    if (!connection) return;

    try {
      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/v1/connections/${connection.id}/endpoints`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log(`Fetched endpoints for ${connection.name}:`, data);

        const endpointsList = data?.endpoints || [];
        if (endpointsList.length > 0) {
          const endpointsByTag: Record<string, number> = {};
          endpointsList.forEach((endpoint: any) => {
            const tags = Array.isArray(endpoint?.tags)
              ? endpoint.tags
              : endpoint?.category
              ? [endpoint.category]
              : [];
            if (tags.length === 0) {
              endpointsByTag["untagged"] =
                (endpointsByTag["untagged"] || 0) + 1;
              return;
            }
            tags.forEach((tag: string) => {
              if (!tag) return;
              endpointsByTag[tag] = (endpointsByTag[tag] || 0) + 1;
            });
          });

          setEndpoints({
            endpoints: endpointsList,
            total: data?.total ?? endpointsList.length,
            endpoints_by_tag: endpointsByTag,
          });
        } else {
          console.log(`No endpoints found for ${connection.name}`);
          setEndpoints(null);
        }
      } else {
        console.error(`Failed to fetch endpoints: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to fetch endpoints:", error);
    }
  };

  const fetchErrorHistory = async () => {
    if (!connection) return;

    try {
      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/v1/connections/${connection.id}/errors`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log(
          `Fetched error history for ${connection.name}:`,
          data.length,
          "errors"
        );
        setErrorHistory(data);
      }
    } catch (error) {
      console.error("Failed to fetch error history:", error);
    }
  };

  const fetchMemories = async () => {
    if (!connection) return;

    try {
      const token = getAuthToken();
      const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      const response = await fetch(
        `${API_URL}/v1/connections/${connection.id}/memories?limit=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMemories(data);
      }
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const fileName = file.name.toLowerCase();
      if (
        !fileName.endsWith(".json") &&
        !fileName.endsWith(".yaml") &&
        !fileName.endsWith(".yml")
      ) {
        alert(
          "Please select a valid OpenAPI spec file (.json, .yaml, or .yml)"
        );
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadSpec = async () => {
    if (!selectedFile || !connection) return;

    setUploading(true);
    setProcessingStatus("Uploading OpenAPI specification...");
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append("file", selectedFile);

      setProcessingStatus("Parsing specification file...");
      const response = await fetch(
        API_ENDPOINTS.connectionUploadSpec(connection.id),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Uploaded spec:", data);

        // Save the spec URL to the connection if base_url is available
        if (data.info?.base_url) {
          setOpenApiUrl(data.info.base_url);
        }

        // Clear the selected file
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        if (data.background_processing) {
          // Endpoints are processed in background - poll for them
          setProcessingStatus(
            `Spec parsed! Processing ${data.info?.endpoint_count || 0} endpoints in background...`
          );

          // Poll a few times to catch endpoints as they appear
          for (let i = 0; i < 4; i++) {
            await new Promise((resolve) => setTimeout(resolve, 3000));
            setProcessingStatus(
              `Waiting for endpoints to be ready... (${i + 1}/4)`
            );
            await fetchEndpoints();
          }
          setProcessingStatus("");
        } else {
          setProcessingStatus("Refreshing endpoint list...");
          await fetchEndpoints();
          setProcessingStatus("");
        }

        alert(
          `Successfully uploaded spec!\n\nFile: ${
            data.filename
          }\nTitle: ${data.info?.title || "Unknown"}\nEndpoints: ${
            data.info?.endpoint_count || 0
          }${data.background_processing ? "\n\nEndpoints are being processed in the background and will appear shortly." : "\n\nEndpoints have been automatically discovered and saved."}`
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to upload spec:", response.status, errorData);
        setProcessingStatus("");
        alert(
          `Failed to upload spec: ${
            errorData.detail || "Unknown error"
          }\n\nStatus: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Error uploading spec:", error);
      setProcessingStatus("");
      alert(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setUploading(false);
      setProcessingStatus("");
    }
  };

  const discoverEndpoints = async () => {
    if (!connection) return;

    setDiscovering(true);
    setProcessingStatus("Fetching OpenAPI specification...");
    try {
      const token = getAuthToken();
      const body: Record<string, string> = {};

      // Include OpenAPI URL if provided
      if (openApiUrl.trim()) {
        body.openapi_url = openApiUrl.trim();
      }

      setProcessingStatus("Discovering endpoints...");
      // Use Next.js API route for consistent proxy handling
      const response = await fetch(
        `/api/v1/connections/${connection.id}/discover-endpoints`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Discovered endpoints:", data);

        const endpointCount = data.endpoints?.length || data.total || 0;
        setProcessingStatus(
          `Processing ${endpointCount} endpoints in background...`
        );

        for (let i = 0; i < 5; i++) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setProcessingStatus(
            `Waiting for endpoints to be ready... (${i + 1}/5)`
          );
          await fetchEndpoints();
          if (endpoints && endpoints.endpoints?.length > 0) break;
        }

        setProcessingStatus("");
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Failed to discover endpoints:",
          response.status,
          errorData
        );
        setProcessingStatus("");
        alert(
          `Failed to discover endpoints: ${
            errorData.detail || "Unknown error"
          }\n\nStatus: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Error discovering endpoints:", error);
      setProcessingStatus("");
      alert(
        `Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setDiscovering(false);
      setProcessingStatus("");
    }
  };

  if (!connection) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[700px] overflow-hidden flex flex-col !left-1/2 !top-1/2 !-translate-x-1/2 !-translate-y-1/2">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {connection.name}
            <Badge
              variant={connection.status === "active" ? "success" : "secondary"}
            >
              {connection.status}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {connection.type} • {connection.auth_type}
          </DialogDescription>
        </DialogHeader>

        {/* Processing Overlay */}
        {(uploading || discovering) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
            <div className="flex flex-col items-center gap-4 p-8 bg-card border rounded-lg shadow-lg">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <h3 className="font-semibold text-lg mb-1">
                  {uploading
                    ? "Processing OpenAPI Spec"
                    : "Discovering Endpoints"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {processingStatus || "Please wait..."}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span>Adding endpoints to database</span>
              </div>
            </div>
          </div>
        )}

        <Tabs key={`${connection.id}-${defaultTab}`} defaultValue={defaultTab} className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="config">
              <Database className="h-4 w-4 mr-2" />
              Config
            </TabsTrigger>
            <TabsTrigger value="endpoints">
              <MapPin className="h-4 w-4 mr-2" />
              {isDatabase ? "Tables" : "Endpoints"}
            </TabsTrigger>
           {/* 
            <TabsTrigger value="tester">
              <Play className="h-4 w-4 mr-2" />
              Test
            </TabsTrigger>
            */}
            <TabsTrigger value="auth">
              <Lock className="h-4 w-4 mr-2" />
              Auth
            </TabsTrigger>
            <TabsTrigger value="errors">
              <History className="h-4 w-4 mr-2" />
              Errors
            </TabsTrigger>
            <TabsTrigger value="raw">
              <Code className="h-4 w-4 mr-2" />
              Raw
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[500px] mt-4">
            <TabsContent value="config" className="space-y-4">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-3">Connection Configuration</h3>
                <pre className="json-display">
                  {JSON.stringify(obfuscateData(connection.config), null, 2)}
                </pre>
              </div>

              {connection.last_test_result && (
                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold mb-3">Last Test Result</h3>
                  <pre className="json-display">
                    {JSON.stringify(
                      obfuscateData(connection.last_test_result),
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
            </TabsContent>

            <TabsContent value="auth" className="space-y-4">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-3">Authentication Type</h3>
                <Badge className="mb-4">{connection.auth_type}</Badge>

                <div className="space-y-3">
                  {connection.config.token_url && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Token URL
                      </p>
                      <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                        {connection.config.token_url}
                      </p>
                    </div>
                  )}

                  {connection.auth_config && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Auth Configuration
                      </p>
                      <div className="space-y-2">
                        {Object.entries(
                          obfuscateData(connection.auth_config)
                        ).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center"
                          >
                            <span className="text-sm">{key}:</span>
                            <span className="text-sm font-mono">
                              {String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="endpoints" className="space-y-4">
              {isDatabase ? (
                <DatabaseSchemaView
                  connection={connection}
                  onSchemaLoaded={(schema) => setDatabaseSchema(schema)}
                  onEndpointsGenerated={() => fetchEndpoints()}
                />
              ) : endpoints ? (
                <div className="rounded-lg border p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">Discovered Endpoints</h3>
                      <div className="flex items-center gap-2">
                        <Badge>{endpoints.total || 0} endpoints</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={discoverEndpoints}
                          disabled={discovering}
                        >
                          {discovering ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Discovering...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Refresh
                            </>
                          )}
                        </Button>
                      </div>
                    </div>


                  </div>

                  {endpoints.endpoints_by_tag && (
                    <div className="space-y-4">
                      {Object.entries(endpoints.endpoints_by_tag).map(
                        ([tag, count]) => (
                          <div
                            key={tag}
                            className="flex items-center justify-between p-2 bg-muted rounded"
                          >
                            <span className="text-sm font-medium capitalize">
                              {tag}
                            </span>
                            <Badge variant="secondary">
                              {String(count)} endpoints
                            </Badge>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {endpoints.endpoints && endpoints.endpoints.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold mb-2">
                        All Endpoints
                      </h4>
                      <ScrollArea className="h-[300px]">
                        <div className="space-y-1">
                          {endpoints.endpoints
                            .slice(0, 50)
                            .map((endpoint: any, i: number) => (
                              <div
                                key={i}
                                className="text-xs font-mono bg-muted p-2 rounded"
                              >
                                <span className="text-blue-600 font-semibold">
                                  {endpoint.method}
                                </span>{" "}
                                {endpoint.path}
                              </div>
                            ))}
                          {endpoints.endpoints.length > 50 && (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              ... and {endpoints.endpoints.length - 50} more
                              endpoints
                            </p>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              ) : connection.config?.openapi_spec_uploaded ? (
                <div className="rounded-lg border p-8 text-center">
                  <Code className="h-12 w-12 mx-auto mb-4 opacity-60 text-teal-400" />
                  <p className="mb-1 font-medium">
                    {connection.config?.openapi_spec_title || "OpenAPI Spec"}{" "}
                    {connection.config?.openapi_spec_version && (
                      <span className="text-muted-foreground font-normal">v{connection.config.openapi_spec_version}</span>
                    )}
                  </p>
                  <p className="mb-6 text-sm text-muted-foreground">
                    {connection.config?.openapi_endpoint_count ?? 0} endpoints in stored spec — not yet processed into the endpoint table.
                  </p>

                  <Button
                    onClick={discoverEndpoints}
                    disabled={discovering}
                    className="w-full max-w-xs"
                  >
                    {discovering ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {processingStatus || "Processing Spec..."}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Process Stored Spec
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border p-8 text-center">
                  <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <p className="mb-6 text-muted-foreground">
                    No endpoint data available
                  </p>

                  <div className="max-w-md mx-auto space-y-4">
                    <div className="text-left space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="openapi-url" className="text-sm">
                            OpenAPI Spec URL
                          </Label>
                          {connection.config?.openapi_url && (
                            <span className="text-xs text-slate-600 font-medium">
                              ✓ Saved
                            </span>
                          )}
                        </div>
                        <Input
                          id="openapi-url"
                          type="url"
                          placeholder="https://api.example.com/openapi.json"
                          value={openApiUrl}
                          onChange={(e) => setOpenApiUrl(e.target.value)}
                          className="text-left"
                        />
                        <p className="text-xs text-muted-foreground">
                          {connection.config?.openapi_url
                            ? "OpenAPI URL is saved. Update and click Discover to save changes."
                            : "Add OpenAPI URL and click Discover to save and discover endpoints."}
                        </p>
                      </div>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Or
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="spec-file-upload-empty"
                          className="text-sm"
                        >
                          Upload OpenAPI Spec File
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            ref={fileInputRef}
                            id="spec-file-upload-empty"
                            type="file"
                            accept=".json,.yaml,.yml"
                            onChange={handleFileSelect}
                            className="text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUploadSpec}
                            disabled={!selectedFile || uploading}
                          >
                            {uploading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Upload a .json, .yaml, or .yml OpenAPI specification
                          file
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={discoverEndpoints}
                      disabled={discovering}
                      className="w-full"
                    >
                      {discovering ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Discovering Endpoints...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Discover Endpoints
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* <TabsContent value="tester" className="space-y-4">
              {isDatabase ? (
                <DatabaseQueryTester
                  connection={connection}
                  tables={
                    databaseSchema?.tables || databaseSchema?.collections || []
                  }
                />
              ) : endpoints &&
                endpoints.endpoints &&
                endpoints.endpoints.length > 0 ? (
                <EndpointTester
                  connectionId={connection.id}
                  baseUrl={connection.config?.base_url || ""}
                  endpoints={endpoints.endpoints}
                  connection={connection}
                />
              ) : (
                <div className="rounded-lg border p-8 text-center">
                  <Play className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <p className="mb-2 text-muted-foreground">
                    No endpoints available to test
                  </p>
                  <p className="text-xs mb-6 text-muted-foreground">
                    Discover endpoints first to use the endpoint tester
                  </p>

                  <div className="max-w-md mx-auto space-y-4">
                    <div className="text-left space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label
                            htmlFor="openapi-url-tester"
                            className="text-sm"
                          >
                            OpenAPI Spec URL
                          </Label>
                          {connection.config?.openapi_url && (
                            <span className="text-xs text-slate-600 font-medium">
                              ✓ Saved
                            </span>
                          )}
                        </div>
                        <Input
                          id="openapi-url-tester"
                          type="url"
                          placeholder="https://api.example.com/openapi.json"
                          value={openApiUrl}
                          onChange={(e) => setOpenApiUrl(e.target.value)}
                          className="text-left"
                        />
                        <p className="text-xs text-muted-foreground">
                          {connection.config?.openapi_url
                            ? "OpenAPI URL is saved. Update and click Discover to save changes."
                            : "Add OpenAPI URL and click Discover to save and discover endpoints."}
                        </p>
                      </div>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Or
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="spec-file-upload-tester"
                          className="text-sm"
                        >
                          Upload OpenAPI Spec File
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            ref={fileInputRef}
                            id="spec-file-upload-tester"
                            type="file"
                            accept=".json,.yaml,.yml"
                            onChange={handleFileSelect}
                            className="text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUploadSpec}
                            disabled={!selectedFile || uploading}
                          >
                            {uploading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Upload a .json, .yaml, or .yml OpenAPI specification
                          file
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={discoverEndpoints}
                      disabled={discovering}
                      className="w-full"
                    >
                      {discovering ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Discovering...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Discover Endpoints
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
            */}
            <TabsContent value="errors" className="space-y-4">
              {errorHistory.length > 0 ? (
                <div className="space-y-3">
                  {errorHistory.map((error) => (
                    <div
                      key={error.id}
                      className="rounded-lg border p-4 bg-red-50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="destructive">{error.error_type}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(error.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm mb-2">{error.error_message}</p>
                      {error.resolution_attempted && (
                        <div className="info-block mt-2">
                          <p className="text-xs font-semibold mb-1">
                            Resolution Attempted:
                          </p>
                          <pre className="json-display">
                            {JSON.stringify(
                              error.resolution_attempted,
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border p-8 text-center text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No errors recorded</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="raw" className="space-y-4">
              <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-3">Raw Connection Data</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Sensitive fields (passwords, tokens, keys) are obfuscated for
                  security.
                </p>
                <pre className="json-display">
                  {JSON.stringify(obfuscateData(connection), null, 2)}
                </pre>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
